import { customerDataAtom, seatingZoneAtom } from '@/core'
import { editableCustomerAtom } from '@/core/store/customer.store'
import { UInput, USelect, USelectOption } from '@/core/components/ui'
import { Cart } from '@/core/components/order'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useAtom, useAtomValue } from 'jotai'
import { customerHelpers } from '@helpers'
import { SEATING_ZONE } from '@/core/constants/seat'
import { useEffect, useState, useCallback } from 'react'
import Payment from '@/core/components/order/Payment'
import { useTranslate } from '@repo/unicity'

const maskPhone = (phone: string): string => {
    if (phone.length <= 3) return phone
    const last3 = phone.slice(-3)
    const masked = '*'.repeat(phone.length - 3)
    return `${masked}${last3}`
}

const maskEmail = (email: string): string => {
    if (email.length <= 3) return email
    const first3 = email.slice(0, 3)
    const masked = '*'.repeat(email.length - 3)
    return `${first3}${masked}`
}

const OrderDetailPage = () => {
    const customer = useAtomValue(customerDataAtom)
    const [editableCustomer, setEditableCustomer] = useAtom(editableCustomerAtom)
    const [selectedZone, setSelectedZone] = useAtom(seatingZoneAtom)
    const { translate } = useTranslate()

    const [displayEmail, setDisplayEmail] = useState('')
    const [displayPhone, setDisplayPhone] = useState('')
    const [originalEmail, setOriginalEmail] = useState('')
    const [originalPhone, setOriginalPhone] = useState('')

    // Initialize editable fields from customer data
    useEffect(() => {
        if (customer && !editableCustomer) {
            const fullName = customerHelpers.getHumanName(customer.humanName, false)
            const phone = customer.mobilePhone || customer.homePhone || customer.workPhone || ''
            const email = customer.email || ''
            setOriginalEmail(email)
            setOriginalPhone(phone)
            setEditableCustomer({
                fullName,
                email,
                phone
            })
        }
    }, [customer, editableCustomer, setEditableCustomer])

    // Sync display values — only mask if value matches original
    useEffect(() => {
        if (editableCustomer) {
            setDisplayEmail(editableCustomer.email === originalEmail ? maskEmail(editableCustomer.email) : editableCustomer.email)
            setDisplayPhone(editableCustomer.phone === originalPhone ? maskPhone(editableCustomer.phone) : editableCustomer.phone)
        }
    }, [editableCustomer?.email, editableCustomer?.phone, originalEmail, originalPhone])

    const handleEmailFocus = useCallback(() => {
        setDisplayEmail('')
    }, [])

    const handleEmailBlur = useCallback(() => {
        if (!editableCustomer) return
        const current = displayEmail.trim()
        setEditableCustomer({ ...editableCustomer, email: current })
    }, [editableCustomer, displayEmail, setEditableCustomer])

    const handlePhoneFocus = useCallback(() => {
        setDisplayPhone('')
    }, [])

    const handlePhoneChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            // Allow only numeric characters
            const numericOnly = e.target.value.replace(/\D/g, '')
            setDisplayPhone(numericOnly)
        },
        []
    )

    const handlePhoneBlur = useCallback(() => {
        if (!editableCustomer) return
        const current = displayPhone.trim()
        setEditableCustomer({ ...editableCustomer, phone: current })
    }, [editableCustomer, displayPhone, setEditableCustomer])

    // Convert SEATING_ZONE to USelectOption array
    const seatingZoneOptions: USelectOption[] = Object.entries(SEATING_ZONE).map(([value, label]) => ({
        value,
        label
    }))

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    return (
        <Stack sx={{ flex: 1 }}>
            <Stack
                p="48px 24px"
                sx={{
                    bgcolor: 'background1.light'
                }}
                gap={3}>
                <Typography variant="h2" fontWeight={700} color="primary.main" textAlign="center" textTransform="uppercase">
                    {translate('personal_information')}
                </Typography>
                {customer && editableCustomer && (
                    <Stack p={3} borderRadius={'12px'} bgcolor={'secondary.main'} gap={3}>
                        <UInput
                            label={translate('fullName')}
                            value={editableCustomer.fullName}
                            onChange={e => setEditableCustomer({ ...editableCustomer, fullName: e.target.value })}
                            onClear={() => setEditableCustomer({ ...editableCustomer, fullName: '' })}
                        />
                        <UInput
                            label={translate('email')}
                            value={displayEmail}
                            onChange={e => setDisplayEmail(e.target.value)}
                            onFocus={handleEmailFocus}
                            onBlur={handleEmailBlur}
                            onClear={() => {
                                setDisplayEmail('')
                                setEditableCustomer({ ...editableCustomer, email: '' })
                            }}
                        />
                        <UInput
                            label={translate('phone_number')}
                            value={displayPhone}
                            onChange={handlePhoneChange}
                            onFocus={handlePhoneFocus}
                            onBlur={handlePhoneBlur}
                            onClear={() => {
                                setDisplayPhone('')
                                setEditableCustomer({ ...editableCustomer, phone: '' })
                            }}
                            type="tel"
                            inputMode="numeric"
                        />
                        <USelect
                            label={translate('seating_zone')}
                            name="seating_zone"
                            value={selectedZone}
                            onChange={setSelectedZone}
                            options={seatingZoneOptions}
                        />
                    </Stack>
                )}
            </Stack>
            <Cart />

            <Payment />
        </Stack>
    )
}

export default OrderDetailPage
